/**
 * Unit-aware value formatter — companion to backend `explore_advanced.format_unit`.
 *
 * Inputs:
 *   - `measureUnits`: `{ field_name: unit_code }` map produced by the server
 *     (`_measure_units` on Explore responses; eventually also on dashboard
 *     query results once that pipeline plumbs units through).
 *
 * Outputs:
 *   - `makeFormatValue(measureUnits, fallback)` — returns a `(val, fieldName)`
 *     function that ChartRenderer + KPI tiles already call with the field
 *     name as the second arg. When the field has a known unit, we format
 *     with that unit's rule; otherwise we delegate to `fallback`.
 *
 * Unit codes mirror the Python constants in `explore_advanced.py`:
 *   USD, USD_thousands, USD_millions, percent, percent_decimal,
 *   ratio, count, days, hours, score, none.
 */

const NBSP = '\u00A0';

function _toNumber(v) {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * Format a single value with a unit code. Returns the original string
 * representation when the value isn't numeric or the unit is unknown.
 */
export function formatUnit(value, unit) {
  if (value === null || value === undefined || value === '') return '';
  const n = _toNumber(value);
  if (n === null) return String(value);

  switch (unit) {
    case 'USD_millions': {
      // Value is expressed in millions. If it would collapse to "$0.0M",
      // show the real underlying dollar amount instead (e.g. 0.000594 → "$594").
      if (n !== 0 && Math.abs(n) < 0.05) {
        const dollars = n * 1_000_000;
        if (Math.abs(dollars) >= 1_000) return `$${(dollars / 1_000).toLocaleString(undefined, { maximumFractionDigits: 1 })}K`;
        return `$${dollars.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
      }
      return `$${n.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 2 })}M`;
    }
    case 'USD_thousands': {
      if (n !== 0 && Math.abs(n) < 0.05) {
        const dollars = n * 1_000;
        return `$${dollars.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
      }
      return `$${n.toLocaleString(undefined, { maximumFractionDigits: 1 })}K`;
    }
    case 'USD': {
      const abs = Math.abs(n);
      if (abs >= 1_000_000) return `$${(n / 1_000_000).toLocaleString(undefined, { maximumFractionDigits: 2 })}M`;
      if (abs >= 10_000) return `$${(n / 1_000).toLocaleString(undefined, { maximumFractionDigits: 1 })}K`;
      return `$${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
    }
    case 'percent':
      return `${n.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
    case 'percent_decimal':
      return `${(n * 100).toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
    case 'ratio':
      return `${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}x`;
    case 'count':
      return Number.isInteger(n)
        ? n.toLocaleString()
        : n.toLocaleString(undefined, { maximumFractionDigits: 1 });
    case 'days':
      return `${n.toLocaleString(undefined, { maximumFractionDigits: 1 })}${NBSP}days`;
    case 'hours':
      return `${n.toLocaleString(undefined, { maximumFractionDigits: 1 })}${NBSP}hrs`;
    case 'score':
      return n.toLocaleString(undefined, { maximumFractionDigits: 1 });
    case 'none':
    default:
      return null; // signal "no unit applied — caller should fall back"
  }
}

/**
 * Compact axis-tick variant — same units but more aggressive scaling so
 * tick labels don't overflow ($1,234,567 → $1.2M).
 */
export function formatUnitCompact(value, unit) {
  const n = _toNumber(value);
  if (n === null) return formatUnit(value, unit);
  const abs = Math.abs(n);

  if (unit === 'USD') {
    if (abs >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(1)}B`;
    if (abs >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
    return `$${n.toFixed(0)}`;
  }
  if (unit === 'count') {
    if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
    return n.toLocaleString();
  }
  // For other units, the regular formatter is already compact enough.
  return formatUnit(value, unit);
}

/**
 * Returns a `(val, fieldName) => string` function that ChartRenderer +
 * KPI tiles use. Uses unit map for measure fields; delegates to fallback
 * for dimensions / unknown fields. `compactAxis` switches to the compact
 * variant — pass `true` for axis ticks, `false` for tooltips and KPIs.
 */
export function makeFormatValue(measureUnits, fallback, { compactAxis = false } = {}) {
  const fmt = compactAxis ? formatUnitCompact : formatUnit;
  return (val, fieldName) => {
    if (fieldName && measureUnits && measureUnits[fieldName]) {
      const out = fmt(val, measureUnits[fieldName]);
      if (out !== null && out !== undefined) return out;
    }
    return fallback ? fallback(val, fieldName) : (val ?? '');
  };
}

/**
 * Human-readable unit label for axis titles and KPI sub-text.
 * Returns null when no decoration is appropriate (counts, scores, etc.)
 * so callers can avoid pointless suffixes.
 */
export function unitAxisSuffix(unit) {
  switch (unit) {
    case 'USD_millions': return '($M)';
    case 'USD_thousands': return '($K)';
    case 'USD': return '($)';
    case 'percent':
    case 'percent_decimal': return '(%)';
    case 'ratio': return '(x)';
    case 'days': return '(days)';
    case 'hours': return '(hrs)';
    default: return null;
  }
}

/**
 * Compact badge symbols for a `_measure_units` map, so a tile can show at a
 * glance how its numbers are scaled. Returns de-duped `{symbol, label}` items
 * (e.g. `$`, `%`, `x`). Unknown/plain units are omitted.
 */
const _UNIT_BADGE = {
  USD: { symbol: '$', label: 'Currency (USD)' },
  USD_thousands: { symbol: '$K', label: 'Currency (thousands)' },
  USD_millions: { symbol: '$M', label: 'Currency (millions)' },
  percent: { symbol: '%', label: 'Percentage' },
  percent_decimal: { symbol: '%', label: 'Percentage' },
  ratio: { symbol: 'x', label: 'Ratio' },
  days: { symbol: 'd', label: 'Days' },
  hours: { symbol: 'h', label: 'Hours' },
  count: { symbol: '#', label: 'Count' },
  score: { symbol: '★', label: 'Score' },
};

export function unitBadges(measureUnits) {
  if (!measureUnits) return [];
  const seen = new Set();
  const out = [];
  for (const unit of Object.values(measureUnits)) {
    const b = _UNIT_BADGE[unit];
    if (b && !seen.has(b.symbol)) {
      seen.add(b.symbol);
      out.push(b);
    }
  }
  return out;
}

export default {
  formatUnit,
  formatUnitCompact,
  makeFormatValue,
  unitAxisSuffix,
  unitBadges,
};
